import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  THERMOMETER_CONFIG,
  THERMOMETER_OPTIONS,
  type ActivityThermometer,
  type ActivityDetail,
} from "@/types/activity";
import { cn } from "@/lib/utils";
import { toUserError } from "@/lib/api-error";

interface CompletarAtividadeModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: ActivityDetail;
}

export function CompletarAtividadeModal({
  isOpen,
  onClose,
  activity,
}: CompletarAtividadeModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [termometro, setTermometro] = useState<ActivityThermometer | null>(null);
  const [conversaWhatsapp, setConversaWhatsapp] = useState("");
  const [resumo, setResumo] = useState("");
  const [observacao, setObservacao] = useState(activity.observacao || "");

  const handleSubmit = async () => {
    if (activity.tipo === "falar_com_frila" && !termometro) {
      toast({
        title: "Selecione o termometro",
        description: "Voce precisa indicar o nivel de interesse do freelancer.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.rpc("complete_freelancer_activity", {
        p_activity_id: activity.id,
        p_termometro: termometro,
        p_conversa_whatsapp: conversaWhatsapp || null,
        p_sentimento: null, // Preenchido pela IA no futuro
        p_resumo: resumo || null,
        p_observacao: observacao || null,
      });

      if (error) throw error;

      toast({
        title: "Atividade concluida",
        description: `Atividade para ${activity.freelancer_nome} foi marcada como concluida.`,
      });

      queryClient.invalidateQueries({ queryKey: ["activity_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      onClose();
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao completar atividade:", error);
      toast({
        title: "Erro",
        description: "Nao foi possivel completar a atividade. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFalarComFrila = activity.tipo === "falar_com_frila";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Completar Atividade</DialogTitle>
          <DialogDescription>
            Registre os detalhes do contato com <strong>{activity.freelancer_nome}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Termometro - apenas para Falar com Frila */}
          {isFalarComFrila && (
            <div className="space-y-3">
              <Label>Termometro de Interesse</Label>
              <div className="flex justify-between gap-2">
                {THERMOMETER_OPTIONS.map((option) => {
                  const config = THERMOMETER_CONFIG[option];
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setTermometro(option)}
                      className={cn(
                        "flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all flex-1",
                        termometro === option
                          ? "border-primary bg-primary/10"
                          : "border-muted hover:border-primary/50"
                      )}
                    >
                      <span className="text-2xl">{config.emoji}</span>
                      <span className="text-xs text-center font-medium">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Conversa do WhatsApp - apenas para Falar com Frila */}
          {isFalarComFrila && (
            <div className="space-y-2">
              <Label htmlFor="conversa-whatsapp">Conversa do WhatsApp</Label>
              <Textarea
                id="conversa-whatsapp"
                placeholder="Cole aqui a conversa do WhatsApp..."
                value={conversaWhatsapp}
                onChange={(e) => setConversaWhatsapp(e.target.value)}
                className="min-h-[120px] font-mono text-xs"
              />
            </div>
          )}

          {/* Resumo */}
          <div className="space-y-2">
            <Label htmlFor="resumo">Resumo</Label>
            <Textarea
              id="resumo"
              placeholder="Faca um breve resumo do contato..."
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          {/* Observacao */}
          <div className="space-y-2">
            <Label htmlFor="observacao">Observacao</Label>
            <Textarea
              id="observacao"
              placeholder="Observacoes adicionais..."
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Salvando..." : "Marcar como Concluida"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
